import { findMatches, vocab } from '@/ds';
import type { Schemas } from '@/lib/api/client';
import { formatNumber } from '@/lib/format/format';
import { recordWarnings } from '@/features/candidates/vocab';

// Match evidence in the design system's words; linking on the review page shares it.

export type Strength = 'likely' | 'possible' | 'unlikely';
export type Result = 'same' | 'close' | 'different' | 'unknown';

export interface Criterion {
  label: string;
  result: Result;
  detail: string;
}

type Match = Schemas['DedupMatch'];
type Group = Schemas['DedupClusterListItemDto'];
type Member = Schemas['DedupClusterMemberDto'];

const DAY_MS = 86_400_000;
const WEAKEST_FIRST: Strength[] = ['unlikely', 'possible', 'likely'];

/** The API's band ("Likely", "Possible", "Unlikely") as the design system's match strength. */
export function toStrength(band: string | null | undefined): Strength {
  const value = band?.toLowerCase();
  return value === 'likely' || value === 'possible' ? value : 'unlikely';
}

export const strengthLabel = (strength: Strength): string =>
  vocab.MATCH[strength]?.label ?? strength;

export function nameCriterion(similarity: number | undefined): Criterion {
  const value = similarity ?? 0;
  return {
    label: 'Name',
    result: value >= 0.85 ? 'same' : value >= 0.5 ? 'close' : 'different',
    detail: `${Math.round(value * 100)}% alike`,
  };
}

/** "Same day", "1 day apart", "12 days apart"; the sign is ignored. */
export function daysApartWords(days: number): string {
  const n = Math.abs(days);
  return n === 0 ? 'Same day' : `${formatNumber(n)} ${n === 1 ? 'day' : 'days'} apart`;
}

export function dateCriterion(a: string, b: string): Criterion {
  const days = Math.round(Math.abs(Date.parse(a) - Date.parse(b)) / DAY_MS);
  const result = days === 0 ? 'same' : days <= 7 ? 'close' : 'different';
  return { label: 'Date', result, detail: daysApartWords(days) };
}

export function placeCriterion(a: string | null, b: string | null): Criterion {
  if (!a || !b) return { label: 'Place', result: 'unknown', detail: 'No city to compare' };
  const same = findMatches(b, a).length > 0 || findMatches(a, b).length > 0;
  return same
    ? { label: 'Place', result: 'same', detail: b }
    : { label: 'Place', result: 'different', detail: `${a} vs ${b}` };
}

/** "5 · 10 · 21.1 km", or null when no race has a distance yet. */
export function formatDistances(distances: readonly (number | null)[]): string | null {
  const known = [...new Set(distances.filter((d): d is number => d !== null))].sort(
    (x, y) => x - y,
  );
  return known.length ? `${known.map(formatNumber).join(' · ')} km` : null;
}

export function distanceCriterion(
  match: Match,
  a: readonly (number | null)[],
  b: readonly (number | null)[],
): Criterion {
  const left = formatDistances(a);
  const right = formatDistances(b);
  if (!match.bothSidesAnnounceADistance || !left || !right) {
    return { label: 'Distances', result: 'unknown', detail: 'Not announced on both' };
  }
  const overlap = match.distanceOverlap ?? 0;
  const result: Result = overlap >= 0.999 ? 'same' : overlap > 0 ? 'close' : 'different';
  return { label: 'Distances', result, detail: result === 'same' ? left : `${left} vs ${right}` };
}

export const isPending = (member: Member) => member.stagedEvent.approvalStatus === 'PendingReview';

/** The API keeps a pending or already published record; anything else stays as it is. */
export const canKeep = (member: Member) =>
  isPending(member) || member.stagedEvent.approvalStatus === 'Approved';

/** A winner that publishes in the same step: pending, no warning, and no suspected event it isn't linked to. */
export function canKeepAndPublish(group: Group, winner: Member | undefined): boolean {
  if (!winner || !isPending(winner)) return false;
  const record = winner.stagedEvent;
  if (recordWarnings(record).some((w) => w !== 'duplicate-suspected')) return false;
  const suspected = group.suspectedEvent?.raceEventId;
  return !suspected || record.linkedRaceEventId === suspected;
}

export interface KeepPlan {
  label: string;
  title: string;
  /** Ends “Keeping “X”…” in the decision bar. */
  sentence: string;
  undoHelp: string;
  /** What a winner that publishes in the same step says at each outcome. */
  publishing?: { id: string; kept: string; failed: string; tookBack: string };
}

/** Every word the keep action uses, whether the winner publishes, links or only stays. */
export function keepPlan(input: {
  kept?: { key: string; name: string };
  publishesOnKeep: boolean;
  linked: boolean;
  target: string;
  losers: number;
  rejected: string;
}): KeepPlan {
  const { kept, publishesOnKeep, linked, target, losers, rejected } = input;
  const name = kept?.name ?? '';
  const reject = plural(losers, 'candidate');
  const undoFirst = linked ? 'approval' : 'publish';
  const label = publishesOnKeep
    ? linked
      ? 'Keep and link'
      : 'Keep and publish'
    : kept
      ? losers
        ? `Keep 1, reject ${formatNumber(losers)}`
        : 'Keep 1'
      : 'Keep 1, reject the rest';
  const title = !publishesOnKeep
    ? `Keep “${name}” and reject ${reject}?`
    : linked
      ? `Keep “${name}” and link it to ${target}, and reject ${reject}?`
      : `Keep and publish “${name}”, and reject ${reject}?`;
  const sentence = !publishesOnKeep ? '' : linked ? ` and linking it to ${target}` : ' and publishing it';
  const undoHelp = publishesOnKeep
    ? `You can undo this from the toast. In the audit trail, undo the ${undoFirst} first, then Reopen group. A publish can be undone for 7 days.`
    : 'You can undo this from the toast, or with Reopen group in the audit trail.';
  const publishing =
    kept && publishesOnKeep
      ? linked
        ? {
            id: kept.key,
            kept: `Kept “${name}” and linked it to ${target}. ${rejected}`,
            failed: `Kept “${name}”, but couldn't link it to ${target}`,
            tookBack: `Unlinked “${name}”`,
          }
        : {
            id: kept.key,
            kept: `Kept “${name}” and published it. ${rejected}`,
            failed: `Kept “${name}”, but couldn't publish it`,
            tookBack: `Took “${name}” out of the app`,
          }
      : undefined;
  return { label, title, sentence, undoHelp, publishing };
}

/** The weakest pair among the records a decision can still touch. */
export function groupStrength(group: Group): Strength | undefined {
  const live = new Set(group.members.filter(canKeep).map((m) => m.stagedEvent.id));
  const bands = [
    ...group.members
      .filter((m) => live.has(m.stagedEvent.id))
      .map((m) => m.scoreAgainstSuspectedEvent?.band),
    ...group.pairwiseScores
      .filter((p) => live.has(p.firstStagedEventId) && live.has(p.secondStagedEventId))
      .map((p) => p.score.band),
  ].filter((band): band is string => !!band);
  if (!bands.length) return undefined;
  return bands
    .map(toStrength)
    .reduce((weakest, s) =>
      WEAKEST_FIRST.indexOf(s) < WEAKEST_FIRST.indexOf(weakest) ? s : weakest,
    );
}

/** The published event's name, else the first candidate's. */
export function groupName(group: Group): string {
  return (
    group.suspectedEvent?.name ??
    (group.members.find(canKeep) ?? group.members[0])?.stagedEvent.name ??
    'Duplicate group'
  );
}

const plural = (n: number, noun: string) => `${formatNumber(n)} ${noun}${n === 1 ? '' : 's'}`;

/** "2 candidates · 1 published": the records a decision touches. */
export function groupRecords(group: Group): string {
  const candidates = plural(group.members.filter(isPending).length, 'candidate');
  return group.suspectedEvent ? `${candidates} · 1 published` : candidates;
}

/** The published event's date and city, else the first candidate's. */
export function groupWhere(group: Group): { date: string | null; city: string | null } {
  const first = group.members.find(canKeep) ?? group.members[0];
  return {
    date: group.suspectedEvent?.date ?? first?.stagedEvent.date ?? null,
    city: group.suspectedEvent?.city ?? first?.stagedEvent.city ?? null,
  };
}
